import type { Person } from '../types.ts';

/**
 * De 100-jaarregel, client-side.
 *
 * Deze regel moet exact gelijk blijven aan `shouldRedactPerson` in
 * `shared/people-utils.js`. Hij stond eerst als losse functie in `ReportsPage.tsx`;
 * daarnaast hadden de PDF-export en de GEDCOM-export elk hun eigen, afwijkende
 * benadering (B-48/B-52). Eén plek, zodat "welke persoon is beschermd?" overal
 * hetzelfde antwoord geeft.
 *
 * Let op de precieze vorm: `isLiving === false` betekent NIET "niet redigeren".
 * Iemand die recent geboren is en géén overlijdensdatum heeft wordt hoe dan ook
 * geredigeerd — de vlag en de leeftijdsdrempel zijn twee onafhankelijke gronden
 * (`isLiving || isRecentlyBorn`).
 *
 * @param redactYears drempel in jaren; standaard 100, gelijk aan de server-default.
 */
/** GEDCOM-`RESN`: vertrouwelijk of privacy — afgeschermd, wat de levensloop ook is. */
const isBeperkt = (person: Person) =>
  person.restriction === 'confidential' || person.restriction === 'privacy';

export function isPrivacyRedactedRaw(person: Person, redactYears = 100): boolean {
  // Een expliciet publiek gemarkeerd persoon is altijd zichtbaar.
  if (person.isPublic === true) return false;

  // #1300 — de GEDCOM-RESN-tak ontbrak hier, terwijl `shouldRedactPerson` in
  // shared/people-utils.js er wél op redigeert. Voor de eigenaar van een project
  // filtert de server niets (die krijgt zijn eigen data ongeschonden), dus deze
  // regel was de énige bescherming in een rapport. Een als vertrouwelijk
  // gemarkeerde persoon stond daardoor met volledige datums en woonplaatsen in
  // het bestand dat naar de klant ging — zonder enige melding.
  if (isBeperkt(person)) {
    return true;
  }

  const isLiving = person.isLiving === true;
  const birthYear = person.birthDate?.match(/\d{4}/)?.[0];
  const hasDeath = !!person.deathDate;
  const currentYear = new Date().getFullYear();
  const isRecentlyBorn =
    !!birthYear && !hasDeath && currentYear - parseInt(birthYear) < redactYears;
  return isLiving || isRecentlyBorn;
}

/**
 * Afgeschermd omdat de persoon (voor zover bekend) leeft — niet om een RESN-beperking
 * en zonder overlijden of begrafenis. Alleen dan is "nog in leven" waar; een
 * vertrouwelijke overledene is wel afgeschermd, maar leeft niet (final review fase 2).
 */
export function afgeschermdAlsLevend(person: Person, redactYears = 100): boolean {
  return isPrivacyRedactedRaw(person, redactYears) && nogInLevenWaar(person);
}

/**
 * #1602 — de helft van `afgeschermdAlsLevend` voor wie al besloten heeft dát iemand
 * is afgeschermd (de plaat via `isHidden`, met de drempel van het rapport): mag er
 * "nog in leven" bij? Nee bij een RESN-beperking en bij overlijden of begrafenis.
 */
export function nogInLevenWaar(person: Person): boolean {
  if (isBeperkt(person) || person.deathDate) return false;
  return !(person.events ?? []).some((e) => e.type === 'death' || e.type === 'burial');
}
